import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSearchResult } from '@/features/pages/types/page.types';

type SearchFunction = (query: string) => PageSearchResult[] | Promise<PageSearchResult[]>;

interface SearchHookConfig {
  searchFn: SearchFunction;
  defaultResults: PageSearchResult[];
  collectionId?: string;
}

interface SearchHookResult {
  searchQuery: string;
  searchResults: PageSearchResult[];
  isModalOpen: boolean;
  setSearchQuery: (query: string) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  onClickPage: (pageId: string) => void;
}

export const useSearchPage = ({
  searchFn,
  defaultResults,
  collectionId,
}: SearchHookConfig): SearchHookResult => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PageSearchResult[]>(defaultResults);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let active = true;

    if (searchQuery) {
      const result = searchFn(searchQuery);
      if (result instanceof Promise) {
        result
          .then(results => {
            if (active) setSearchResults(results);
          })
          .catch(() => {
            if (active) setSearchResults(defaultResults);
          });
      } else {
        setSearchResults(result);
      }
    } else {
      setSearchResults(defaultResults);
    }

    return () => {
      active = false;
    };
  }, [searchQuery, searchFn, collectionId, defaultResults]);

  const onClickPage = (pageId: string, itemCollectionId?: string) => {
    if (itemCollectionId && location.pathname.includes('/shared')) {
      navigate(`/shared/${itemCollectionId}/page/${pageId}`);
      return;
    }
    navigate(
      itemCollectionId ? `/collection/${itemCollectionId}/page/${pageId}` : `/page/${pageId}`
    );
    setIsModalOpen(false);
  };

  return {
    searchQuery,
    searchResults,
    isModalOpen,
    setSearchQuery,
    setIsModalOpen,
    onClickPage,
  };
};
